import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";
import type { SWRConfiguration } from "swr";

import type { GetGroupIdentityByIdResponse } from "src/api/Tmd.dtos";
import GroupIdentityContext from "src/contexts/data/GroupIdentityContext";
import useGroupIdentityById from "src/hooks/data/load/useGroupIdentityById";

/**
 * Configures the group identity request and the descendants receiving its state.
 */
interface IGroupProviderProps extends PropsWithChildren {
    id?: number;
    configuration?: SWRConfiguration<GetGroupIdentityByIdResponse, GetGroupIdentityByIdResponse>;
}

/**
 * Publishes the group identity loader state to descendants.
 *
 * Postconditions: Children receive the current loader result, including loading and error state;
 * rendering this provider does not guarantee a completed request. Children remain rendered while
 * loading or on failure. The supplied ID and SWR configuration pass through unchanged.
 */
const GroupIdentityProvider: FC<IGroupProviderProps> = ({ children, id, configuration }) => {
    const context = useGroupIdentityById(id, configuration);

    return (
        <GroupIdentityContext.Provider value={context}>{children}</GroupIdentityContext.Provider>
    );
};

/**
 * Loads group identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RouteGroupIdentityProvider: FC<PropsWithChildren> = ({ children }) => {
    const { groupId } = useParams<{ groupId: string }>();
    const id = groupId ? parseInt(groupId, 10) : undefined;

    return <GroupIdentityProvider id={id}>{children}</GroupIdentityProvider>;
};

export default GroupIdentityProvider;
